import { useEffect, useState } from 'react';
import { Banner, BannerApiResponse, BannerCreateResponse, BannerUpdateResponse, BannerToggleResponse } from '../types/banner';
import { Image, Plus, Pencil, Trash2, ExternalLink, Eye, EyeOff } from 'lucide-react';
import BannerModal from '../components/BannerModal';
import Toast from '../components/Toast';
import Modal from '../components/Modal';

const API_URL = 'https://api.epsonrewards.my/v1/api/banners';
const API_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoxMjMsImV4cCI6NDg4NzIyODgyNn0.vWUOB7q8p28_b1Yl0sRTPYRoefEZ5iOXhgjDHAhESlI';

export default function Banners() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState({ show: false, message: '' });
  const [toastId, setToastId] = useState<number | null>(null);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    banner: Banner | null;
    isEdit: boolean;
  }>({
    isOpen: false,
    banner: null,
    isEdit: false,
  });
  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    banner: Banner | null;
  }>({
    isOpen: false,
    banner: null,
  });
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const fetchBanners = async (activeOnly = false) => {
    try {
      setIsLoading(true);
      setError(null);

      let url = API_URL;
      if (activeOnly) {
        url += '?active_only=true';
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch banners: ${response.status} ${response.statusText}`);
      }

      const result: BannerApiResponse = await response.json();
      
      if (result.success) {
        setBanners(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch banners');
      }
    } catch (err) {
      console.error('Error fetching banners:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners(showActiveOnly);
  }, [showActiveOnly]);

  const handleCreateBanner = () => {
    setModalState({
      isOpen: true,
      banner: null,
      isEdit: false,
    });
  };

  const handleEditBanner = (banner: Banner) => {
    setModalState({
      isOpen: true,
      banner,
      isEdit: true,
    });
  };

  const handleDeleteBanner = (banner: Banner) => {
    setConfirmDelete({
      isOpen: true,
      banner,
    });
  };

  const handleToggleActive = async (banner: Banner) => {
    try {
      const newStatus = banner.is_active === 1 || banner.is_active === true ? false : true;
      const response = await fetch(`${API_URL}/${banner.id}/toggle-status`, {
        method: 'POST',  // Changed from PATCH to POST
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      if (!response.ok) {
        throw new Error(`Failed to toggle banner status: ${response.status} ${response.statusText}`);
      }

      const result: BannerToggleResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to toggle banner status');
      }
      
      showToast(`Banner ${result.data.is_active ? 'activated' : 'deactivated'} successfully`);
      fetchBanners(showActiveOnly);
    } catch (error) {
      console.error('Error toggling banner status:', error);
      showToast('Banner status update failed');
    }
  };

  const handleViewImage = (imageUrl: string) => {
    setPreviewImage(imageUrl);
  };

  const handleSaveBanner = async (bannerData: Partial<Banner>) => {
    try {
      if (modalState.isEdit && modalState.banner) {
        await updateBanner(modalState.banner.id, bannerData);
        showToast('Banner updated successfully');
      } else {
        await createBanner(bannerData);
        showToast('Banner created successfully');
      }
      
      fetchBanners(showActiveOnly);
    } catch (error) {
      console.error('Error saving banner:', error);
      showToast('Failed to save banner');
    }
  };

  const createBanner = async (bannerData: Partial<Banner>): Promise<void> => {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bannerData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create banner: ${response.status} ${response.statusText}`);
    }

    const result: BannerCreateResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to create banner');
    }
  };

  const updateBanner = async (id: number, bannerData: Partial<Banner>): Promise<void> => {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${API_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bannerData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update banner: ${response.status} ${response.statusText}`);
    }

    const result: BannerUpdateResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to update banner');
    }
  };

  const deleteBanner = async (id: number): Promise<void> => {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${API_TOKEN}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete banner: ${response.status} ${response.statusText}`);
    }

    const result: BannerUpdateResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to delete banner');
    }
  };

  const confirmDeleteBanner = async () => {
    if (!confirmDelete.banner) return;
    
    try {
      await deleteBanner(confirmDelete.banner.id);
      showToast('Banner deleted successfully');
      fetchBanners(showActiveOnly);
    } catch (error) {
      console.error('Error deleting banner:', error);
      showToast('Failed to delete banner');
    } finally {
      setConfirmDelete({ isOpen: false, banner: null });
    }
  };

  const showToast = (message: string) => {
    if (toastId) {
      clearTimeout(toastId);
    }
    
    setToast({
      show: true,
      message
    });
    
    const id = window.setTimeout(() => {
      setToast({ show: false, message: '' });
    }, 3000);
    
    setToastId(id);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors duration-200">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Banner Management</h1>
          <div className="flex space-x-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="activeOnly"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="activeOnly" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Show active only
              </label>
            </div>
            <button
              onClick={handleCreateBanner}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="h-4 w-4" />
              Add Banner
            </button>
          </div>
        </div>
        
        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded-md">
            <p>{error}</p>
          </div>
        )}
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-12">
            <Image className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-200">No banners found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating a new banner.
            </p>
            <div className="mt-6">
              <button
                onClick={handleCreateBanner}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Add Banner
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 transition-colors duration-200">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Image
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Order
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {banners.map((banner) => (
                  <tr key={banner.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
                          {banner.image_url ? (
                            <img
                              src={banner.image_url}
                              alt={banner.title}
                              className="h-full w-full object-cover object-center"
                              onClick={() => handleViewImage(banner.image_url)}
                            />
                          ) : (
                            <div className="h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                              <Image className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleViewImage(banner.image_url)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          title="View Image"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{banner.title}</div>
                      {banner.link_url && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {banner.link_url}
                          </a>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {banner.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          banner.is_active === 1 || banner.is_active === true
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}
                      >
                        {banner.is_active === 1 || banner.is_active === true ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {banner.display_order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(banner.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleToggleActive(banner)}
                          className={`text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white`}
                          title={banner.is_active === 1 || banner.is_active === true ? 'Deactivate' : 'Activate'}
                        >
                          {banner.is_active === 1 || banner.is_active === true ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEditBanner(banner)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          title="Edit"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteBanner(banner)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Banner Modal for Create/Edit */}
      <BannerModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, banner: null, isEdit: false })}
        banner={modalState.banner}
        onSave={handleSaveBanner}
        title={modalState.isEdit ? 'Edit Banner' : 'Add New Banner'}
      />
      
      {/* Confirm Delete Modal */}
      <Modal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, banner: null })}
      >
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Confirm Delete</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Are you sure you want to delete the banner "{confirmDelete.banner?.title}"? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setConfirmDelete({ isOpen: false, banner: null })}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDeleteBanner}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Image Preview Modal */}
      <Modal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      >
        <div className="flex items-center justify-center p-4">
          <img
            src={previewImage || ''}
            alt="Banner Preview"
            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-lg"
          />
        </div>
      </Modal>
      
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          onClose={() => setToast({show: false, message: ''})}
          id={toastId !== null ? String(toastId) : '0'}
        />
      )}
    </div>
  );
}
